/* Standard hero for secondary pages: eyebrow chip + display title +
 * subtitle + optional CTA row, on either the cream canvas (with two
 * drifting pastel blobs) or a solid ink band for sober pages (legal).
 *
 * `title` is a ReactNode so callers can mark accent words with
 * <Accent> — the serif-italic highlight of the warm-editorial
 * language. Keep accents to 1-2 words per title.
 */
import Chip from './Chip';
import Reveal from './Reveal';

const VARIANTS = {
  cream: {
    section: 'bg-cream-100',
    title: 'text-ink',
    subtitle: 'text-ink-500',
  },
  ink: {
    section: 'bg-ink',
    title: 'text-cream-50',
    subtitle: 'text-cream-50/70',
  },
};

export default function PageHero({
  variant = 'cream',
  eyebrow,
  eyebrowIcon,
  eyebrowTone = 'peach',
  title,
  subtitle,
  children,
}) {
  const styles = VARIANTS[variant] || VARIANTS.cream;

  return (
    <section className={`relative overflow-hidden px-4 ${styles.section}`}>
      {variant === 'cream' && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-16 top-10 h-40 w-40 rounded-full bg-pastel-peach motion-safe:animate-float-slow"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-14 -bottom-10 h-36 w-36 rounded-full bg-pastel-sage motion-safe:animate-float-slower"
          />
        </>
      )}
      <Reveal className="relative mx-auto max-w-4xl py-16 text-center sm:py-20">
        {eyebrow && (
          <Chip tone={eyebrowTone} icon={eyebrowIcon} className="mb-5">
            {eyebrow}
          </Chip>
        )}
        <h1 className={`font-display text-4xl font-bold leading-[1.07] tracking-tight sm:text-5xl ${styles.title}`}>
          {title}
        </h1>
        {subtitle && (
          <p className={`mx-auto mt-4 max-w-2xl text-base leading-relaxed sm:text-lg ${styles.subtitle}`}>
            {subtitle}
          </p>
        )}
        {children && (
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {children}
          </div>
        )}
      </Reveal>
    </section>
  );
}

// Serif-italic accent for 1-2 words inside a display title. Defaults to
// the brand orange; on ink heroes pass a lighter class, e.g.
// className="text-primary-300".
export function Accent({ className = 'text-primary-600', children }) {
  return <em className={`font-accent italic ${className}`}>{children}</em>;
}
